import React from "react";

export type SidebarSize = "small" | "medium" | "large";

type SidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: SidebarSize;
  closeIconSrc?: string;
  scrollable?: boolean;
  /** When false, only the header close button dismisses the sidebar (not backdrop clicks). */
  closeOnBackdropClick?: boolean;
};

const widthClassMap: Record<SidebarSize, string> = {
  small: "w-full max-w-[520px]",
  medium: "w-full max-w-[720px]",
  large: "w-full max-w-[920px]",
};

export default function Sidebar({
  isOpen,
  onClose,
  title,
  children,
  size = "medium",
  closeIconSrc = "/icons/x_circle.svg",
  scrollable = true,
  closeOnBackdropClick = true,
}: SidebarProps) {
  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay fixed inset-0 z-[10000] bg-black/[0.31] backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="fixed inset-0"
        {...(closeOnBackdropClick ? { onClick: onClose } : {})}
        aria-hidden="true"
      />

      <aside
        className={`modal-container fixed right-0 top-0 flex h-[100dvh] flex-col bg-white shadow-sm ${
          scrollable ? "overflow-hidden" : "overflow-y-hidden"
        } ${widthClassMap[size]}`}
        onClick={(e) => {
          // Prevent overlay click from closing when interacting inside.
          e.stopPropagation();
        }}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border-grey px-4 py-4 sm:px-6">
          <h2 className="text-md font-semibold text-dark-black">{title || ""}</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-primary transition-colors"
          >
            <img src={closeIconSrc} alt="" aria-hidden="true" className="h-6 w-6" />
          </button>
        </div>

        <div
          className={
            scrollable
              ? "min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6"
              : "px-4 py-5 sm:px-6"
          }
        >
          {children}
        </div>
      </aside>
    </div>
  );
}

